import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchWithAuth } from "services/api";
import { MdArrowBack, MdEdit, MdSave } from "react-icons/md";
import Card from "components/card";

interface QuestionnaireAssignment {
  id: number;
  completed_at: string;
  questionnaire: {
    id: number;
    name: string;
    code: string;
  };
}

interface Report {
  id: number;
  title: string | null;
  status: string;
  is_final: boolean;
  final_report: string;
  prompt: string | null;
  professional_notes: string | null;
  created_at: string;
  updated_at: string;
  patient: {
    id: number;
    name: string;
    email: string;
  };
  psychologist: {
    id: number;
    name: string;
    email: string;
  };
  questionnaire_assignments: QuestionnaireAssignment[];
}

const ReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [editedReport, setEditedReport] = useState<{
    final_report: string;
    prompt: string;
    professional_notes: string;
  }>({
    final_report: "",
    prompt: "",
    professional_notes: "",
  });

  useEffect(() => {
    const fetchReport = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await fetchWithAuth(`/reports/${id}`);

        if (response.success) {
          setReport(response.data);
          setEditedReport({
            final_report: response.data.final_report,
            prompt: response.data.prompt || "",
            professional_notes: response.data.professional_notes || "",
          });
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError("Error al cargar el informe");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`/reports/${id}`, {
        method: "PUT",
        body: JSON.stringify(editedReport),
      });

      if (response.success) {
        setReport(response.data);
        setEditing(false);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Error al guardar los cambios");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      const response = await fetchWithAuth(`/reports/${id}/regenerate`, {
        method: "POST",
        body: JSON.stringify(editedReport),
      });

      if (response.success) {
        setReport(response.data);
        setEditedReport({
          final_report: response.data.final_report,
          prompt: response.data.prompt || "",
          professional_notes: response.data.professional_notes || "",
        });
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Error al regenerar el informe");
      console.error(err);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-2 border-brand-500"></div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-lg bg-red-100 p-4 text-red-700">
          {error || "No se encontró el informe"}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 grid h-full gap-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/admin/patients/${report.patient.id}`)}
            className="rounded-lg p-2 text-navy-700 transition-all hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700"
          >
            <MdArrowBack className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
              {report.title || "Informe sin título"}
            </h1>
            <p className="text-sm text-gray-600">
              Paciente: {report.patient.name}
            </p>
          </div>
        </div>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
          >
            <MdEdit className="h-5 w-5" />
            <span>Editar</span>
          </button>
        )}
      </div>

      {/* Main Content */}
      <Card extra="w-full p-6">
        {/* Report Status */}
        <div className="mb-6 flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Estado:
          </span>
          <span
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              report.status === "completed"
                ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
            }`}
          >
            {report.status === "completed" ? "Completado" : "En progreso"}
          </span>
        </div>

        {/* Questionnaires */}
        <div className="mb-6">
          <h3 className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
            Cuestionarios incluidos:
          </h3>
          <div className="flex flex-wrap gap-2">
            {report.questionnaire_assignments.map((assignment) => (
              <span
                key={assignment.id}
                className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 dark:bg-navy-700 dark:text-gray-300"
              >
                {assignment.questionnaire.name} ({assignment.questionnaire.code}
                )
              </span>
            ))}
          </div>
        </div>

        {/* Final Report */}
        <div className="mb-6">
          <h3 className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
            Informe Final
          </h3>
          {editing ? (
            <textarea
              value={editedReport.final_report}
              onChange={(e) =>
                setEditedReport((prev) => ({
                  ...prev,
                  final_report: e.target.value,
                }))
              }
              rows={15}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-navy-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500"
            />
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-900">
              <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {report.final_report}
              </p>
            </div>
          )}
        </div>

        {/* Prompt */}
        <div className="mb-6">
          <h3 className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
            Prompt
          </h3>
          {editing ? (
            <textarea
              value={editedReport.prompt}
              onChange={(e) =>
                setEditedReport((prev) => ({
                  ...prev,
                  prompt: e.target.value,
                }))
              }
              rows={6}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-navy-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500"
            />
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-900">
              <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {report.prompt}
              </p>
            </div>
          )}
        </div>

        {/* Professional Notes */}
        <div className="mb-6">
          <h3 className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
            Notas del Profesional
          </h3>
          {editing ? (
            <textarea
              value={editedReport.professional_notes}
              onChange={(e) =>
                setEditedReport((prev) => ({
                  ...prev,
                  professional_notes: e.target.value,
                }))
              }
              rows={6}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-navy-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500"
            />
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-900">
              <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {report.professional_notes}
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          {editing ? (
            <>
              <button
                onClick={() => {
                  setEditing(false);
                  setEditedReport({
                    final_report: report.final_report,
                    prompt: report.prompt || "",
                    professional_notes: report.professional_notes || "",
                  });
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <MdSave className="h-5 w-5" />
                    <span>Guardar Cambios</span>
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {regenerating ? (
                <>
                  <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                  <span>Regenerando...</span>
                </>
              ) : (
                <span>Regenerar Informe</span>
              )}
            </button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ReportDetail;
